import * as echarts from 'echarts/core';
import { BarChart, LineChart, ScatterChart } from 'echarts/charts';
import {
  DataZoomComponent,
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import {
  APP_VERSION,
  buildChartOption,
  buildOptionFromDocument,
  buildPreview,
  createDefaultConfig,
  createSavedDocument,
  parseDocument,
  serializeDocument,
  validateConfig,
} from './model.js';
import { executeSqlQuery } from './sql-client.js';
import {
  SQL_PRESETS,
  createPassthroughQueryResult,
} from './sql-query.js';
import './styles.css';

echarts.use([
  BarChart,
  LineChart,
  ScatterChart,
  DataZoomComponent,
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  CanvasRenderer,
]);

const STORAGE_KEY = 'freefactor.echarts.document.v2';
const MAX_JSON_PREVIEW = 24000;

document.documentElement.dataset.appVersion = APP_VERSION;

const elements = {
  form: document.querySelector('#config-form'),
  validation: document.querySelector('#validation-message'),
  save: document.querySelector('#save-button'),
  draftChart: document.querySelector('#draft-chart'),
  savedChart: document.querySelector('#saved-chart'),
  savedPlaceholder: document.querySelector('#saved-placeholder'),
  savedStatus: document.querySelector('#saved-status'),
  documentMeta: document.querySelector('#document-meta'),
  jsonOutput: document.querySelector('#json-output'),
  jsonSize: document.querySelector('#json-size'),
  open: document.querySelector('#open-button'),
  copy: document.querySelector('#copy-button'),
  download: document.querySelector('#download-button'),
  fileInput: document.querySelector('#file-input'),
};

const draftChart = echarts.init(elements.draftChart, null, { renderer: 'canvas' });
const savedChart = echarts.init(elements.savedChart, null, { renderer: 'canvas' });

let config = createDefaultConfig();
let savedDocument = null;
let serializedDocument = '';
let renderTimer = null;
let renderSequence = 0;
let latestPreparedPreview = null;

function persistJson(json) {
  try {
    window.localStorage.setItem(STORAGE_KEY, json);
    return true;
  } catch {
    return false;
  }
}

function readPersistedJson() {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function discardPersistedJson() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Saving still works in memory when browser storage is unavailable.
  }
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function formatUtcInput(ms) {
  return new Date(ms).toISOString().slice(0, 16);
}

function optionTags(options, selected) {
  return options
    .map(([value, label]) => `<option value="${escapeHtml(value)}" ${value === selected ? 'selected' : ''}>${escapeHtml(label)}</option>`)
    .join('');
}

function field({ label, path, value, type = 'text', valueType = 'string', min, max, step, hint }) {
  const attributes = [
    `type="${type}"`,
    `data-path="${escapeHtml(path)}"`,
    `data-value-type="${valueType}"`,
    `value="${escapeHtml(value ?? '')}"`,
    min !== undefined ? `min="${min}"` : '',
    max !== undefined ? `max="${max}"` : '',
    step !== undefined ? `step="${step}"` : '',
  ].filter(Boolean).join(' ');

  return `<label class="field"><span>${escapeHtml(label)}</span><input ${attributes} />${hint ? `<small>${escapeHtml(hint)}</small>` : ''}</label>`;
}

function toggle({ label, path, checked, hint }) {
  return `<label class="toggle"><span><strong>${escapeHtml(label)}</strong>${hint ? `<small>${escapeHtml(hint)}</small>` : ''}</span><input type="checkbox" data-path="${escapeHtml(path)}" data-value-type="boolean" ${checked ? 'checked' : ''} /><i aria-hidden="true"></i></label>`;
}

function selectField({ label, path, value, options, hint }) {
  return `<label class="field"><span>${escapeHtml(label)}</span><select data-path="${escapeHtml(path)}" data-value-type="string">${optionTags(options, value)}</select>${hint ? `<small>${escapeHtml(hint)}</small>` : ''}</label>`;
}

function sqlEditor() {
  return `<textarea class="sql-editor" data-path="sql.query" data-value-type="string" spellcheck="false" aria-label="SQL query">${escapeHtml(config.sql.query)}</textarea>`;
}

function renderSeriesCard(series, index) {
  const canRemove = config.series.length > 1;
  return `
    <article class="series-card" data-series-id="${escapeHtml(series.id)}">
      <header>
        <span class="series-swatch" aria-hidden="true"></span>
        <div><small>SERIES ${String(index + 1).padStart(2, '0')}</small><strong>${escapeHtml(series.name || 'Untitled')}</strong></div>
        <button type="button" class="icon-button" data-action="remove-series" data-index="${index}" ${canRemove ? '' : 'disabled'} aria-label="Remove ${escapeHtml(series.name)}">×</button>
      </header>
      <div class="control-grid two">
        ${field({ label: 'Name', path: `series.${index}.name`, value: series.name })}
        ${selectField({
          label: 'Chart type',
          path: `series.${index}.type`,
          value: series.type,
          options: [['line', 'Line'], ['area', 'Area'], ['step', 'Step'], ['bar', 'Bar'], ['scatter', 'Scatter']],
        })}
        ${field({ label: 'Color', path: `series.${index}.color`, value: series.color, type: 'color' })}
        ${selectField({
          label: 'Marker',
          path: `series.${index}.symbol`,
          value: series.symbol,
          options: [['none', 'None'], ['circle', 'Circle'], ['rect', 'Square'], ['triangle', 'Triangle'], ['diamond', 'Diamond']],
        })}
        ${selectField({
          label: 'Step position',
          path: `series.${index}.step`,
          value: series.step,
          options: [['start', 'Start'], ['middle', 'Middle'], ['end', 'End']],
        })}
        ${field({ label: 'Line width', path: `series.${index}.lineWidth`, value: series.lineWidth, type: 'number', valueType: 'number', min: 1, max: 10, step: 1 })}
        ${field({ label: 'Marker size', path: `series.${index}.symbolSize`, value: series.symbolSize, type: 'number', valueType: 'number', min: 2, max: 24, step: 1 })}
        ${field({ label: 'Area opacity', path: `series.${index}.areaOpacity`, value: series.areaOpacity, type: 'number', valueType: 'number', min: 0, max: 1, step: 0.05 })}
        ${toggle({ label: 'Smooth curve', path: `series.${index}.smooth`, checked: series.smooth })}
        ${toggle({ label: 'Visible', path: `series.${index}.visible`, checked: series.visible })}
      </div>
      <div class="generator-label"><span>FAKE DATA GENERATOR</span><i></i></div>
      <div class="control-grid three">
        ${field({ label: 'Baseline', path: `series.${index}.generator.baseline`, value: series.generator.baseline, type: 'number', valueType: 'number', step: 1 })}
        ${field({ label: 'Amplitude', path: `series.${index}.generator.amplitude`, value: series.generator.amplitude, type: 'number', valueType: 'number', min: 0, step: 1 })}
        ${field({ label: 'Noise', path: `series.${index}.generator.noise`, value: series.generator.noise, type: 'number', valueType: 'number', min: 0, step: 0.5 })}
        ${field({ label: 'Period (hours)', path: `series.${index}.generator.periodMs`, value: series.generator.periodMs / 3600000, type: 'number', valueType: 'hours-ms', min: 0.25, max: 8760, step: 0.25 })}
        ${field({ label: 'Trend / hour', path: `series.${index}.generator.trendPerHour`, value: series.generator.trendPerHour, type: 'number', valueType: 'number', step: 0.1 })}
        ${field({ label: 'Phase', path: `series.${index}.generator.phase`, value: series.generator.phase, type: 'number', valueType: 'number', min: 0, max: 6.283, step: 0.1 })}
      </div>
    </article>`;
}

function renderInspector() {
  elements.form.innerHTML = `
    <section class="config-section">
      <div class="section-title"><span>CHART</span><i></i><small>Global presentation</small></div>
      <div class="control-grid two">
        ${field({ label: 'Title', path: 'title', value: config.title })}
        ${field({ label: 'Subtitle', path: 'subtitle', value: config.subtitle })}
        ${field({ label: 'Canvas color', path: 'backgroundColor', value: config.backgroundColor, type: 'color' })}
        ${selectField({ label: 'Legend position', path: 'legend.position', value: config.legend.position, options: [['top', 'Top'], ['bottom', 'Bottom'], ['left', 'Left'], ['right', 'Right']] })}
        ${selectField({ label: 'Tooltip trigger', path: 'tooltip.trigger', value: config.tooltip.trigger, options: [['axis', 'Shared axis'], ['item', 'Single item']] })}
        ${field({ label: 'Animation (ms)', path: 'animation.duration', value: config.animation.duration, type: 'number', valueType: 'number', min: 0, max: 5000, step: 50 })}
        ${field({ label: 'Zoom start (%)', path: 'dataZoom.start', value: config.dataZoom.start, type: 'number', valueType: 'number', min: 0, max: 99, step: 1 })}
        ${field({ label: 'Zoom end (%)', path: 'dataZoom.end', value: config.dataZoom.end, type: 'number', valueType: 'number', min: 1, max: 100, step: 1 })}
      </div>
      <div class="toggle-grid">
        ${toggle({ label: 'Legend', path: 'legend.show', checked: config.legend.show, hint: 'Show series labels' })}
        ${toggle({ label: 'Tooltip', path: 'tooltip.show', checked: config.tooltip.show, hint: 'Inspect points' })}
        ${toggle({ label: 'Grid lines', path: 'grid.show', checked: config.grid.show, hint: 'Axis split lines' })}
        ${toggle({ label: 'Animation', path: 'animation.enabled', checked: config.animation.enabled, hint: 'Animate updates' })}
        ${toggle({ label: 'Data zoom', path: 'dataZoom.show', checked: config.dataZoom.show, hint: 'Slider + wheel zoom' })}
      </div>
    </section>

    <section class="config-section sql-section">
      <div class="section-title"><span>SQL QUERY</span><i></i><small>SQLite · read-only</small></div>
      <div class="sql-intro">
        ${toggle({ label: 'Apply SQL', path: 'sql.enabled', checked: config.sql.enabled, hint: 'Query before charting' })}
        <div class="sql-schema">
          <strong>readings</strong>
          <code>timestamp · timestamp_iso · series_id · series_name · value</code>
          <small>Parameters: $start_ms · $end_ms · $interval_ms</small>
        </div>
      </div>
      ${sqlEditor()}
      <div class="sql-toolbar">
        <span>LOAD EXAMPLE</span>
        <button class="quiet-button" type="button" data-action="sql-preset" data-preset="raw">Raw rows</button>
        <button class="quiet-button" type="button" data-action="sql-preset" data-preset="hourly">Hourly average</button>
        <button class="quiet-button" type="button" data-action="sql-preset" data-preset="filtered">WHERE value ≥ 45</button>
      </div>
      <div id="sql-status" class="sql-status" role="status" aria-live="polite">
        <i></i><span>Preparing SQLite…</span>
      </div>
    </section>

    <section class="config-section">
      <div class="section-title"><span>TIME + AXES</span><i></i><small>UTC timeline and scale</small></div>
      <div class="control-grid two">
        ${field({ label: 'Start (UTC)', path: 'time.startMs', value: formatUtcInput(config.time.startMs), type: 'datetime-local', valueType: 'utc-datetime' })}
        ${field({ label: 'End (UTC)', path: 'time.endMs', value: formatUtcInput(config.time.endMs), type: 'datetime-local', valueType: 'utc-datetime' })}
        ${field({ label: 'Sample interval (minutes)', path: 'time.intervalMs', value: config.time.intervalMs / 60000, type: 'number', valueType: 'minutes-ms', min: 1, max: 10080, step: 1 })}
        ${field({ label: 'Generator seed', path: 'seed', value: config.seed, type: 'number', valueType: 'integer', min: 0, max: 2147483647, step: 1 })}
        ${field({ label: 'Y minimum', path: 'yAxis.min', value: config.yAxis.min, type: 'number', valueType: 'nullable-number', hint: 'Blank = automatic' })}
        ${field({ label: 'Y maximum', path: 'yAxis.max', value: config.yAxis.max, type: 'number', valueType: 'nullable-number', hint: 'Blank = automatic' })}
        ${field({ label: 'Y axis label', path: 'yAxis.label', value: config.yAxis.label })}
      </div>
      <div class="toggle-grid compact">
        ${toggle({ label: 'Scale from data', path: 'yAxis.scale', checked: config.yAxis.scale, hint: 'Do not force zero' })}
      </div>
      <div class="inline-actions">
        <button class="quiet-button" type="button" data-action="regenerate">New fake data</button>
        <button class="quiet-button" type="button" data-action="reset">Reset defaults</button>
      </div>
    </section>

    <section class="config-section">
      <div class="section-title"><span>SERIES</span><i></i><small>${config.series.length} of 8</small></div>
      <div class="series-stack">${config.series.map(renderSeriesCard).join('')}</div>
      <button class="add-series-button" type="button" data-action="add-series" ${config.series.length >= 8 ? 'disabled' : ''}><span>＋</span> Add another series</button>
    </section>`;
}

function setAtPath(target, path, value) {
  const parts = path.split('.');
  let cursor = target;
  for (let index = 0; index < parts.length - 1; index += 1) {
    cursor = cursor[parts[index]];
  }
  cursor[parts.at(-1)] = value;
}

function readInputValue(input) {
  switch (input.dataset.valueType) {
    case 'boolean': return input.checked;
    case 'integer': return Number.parseInt(input.value, 10);
    case 'number': return Number(input.value);
    case 'nullable-number': return input.value.trim() === '' ? null : Number(input.value);
    case 'minutes-ms': return Number(input.value) * 60000;
    case 'hours-ms': return Number(input.value) * 3600000;
    case 'utc-datetime': return Date.parse(`${input.value}:00Z`);
    default: return input.value;
  }
}

function errorMessages(validation) {
  return (validation?.errors ?? []).map((error) => typeof error === 'string' ? error : error.message ?? String(error));
}

async function renderDraft() {
  const sequence = ++renderSequence;
  const validation = validateConfig(config);
  const errors = errorMessages(validation);
  const valid = validation?.valid !== false && errors.length === 0;
  elements.save.disabled = true;
  latestPreparedPreview = null;

  if (!valid) {
    elements.validation.className = 'validation-message visible error';
    elements.validation.textContent = errors[0] || 'Check the highlighted configuration values.';
    return;
  }

  try {
    const sourcePreview = buildPreview(config);
    const sqlResult = sourcePreview.config.sql.enabled
      ? await executeSqlQuery(sourcePreview.config, sourcePreview.snapshot)
      : createPassthroughQueryResult(sourcePreview.config, sourcePreview.snapshot);
    if (sequence !== renderSequence) return;
    const option = buildChartOption(sourcePreview.config, sqlResult.snapshot);
    latestPreparedPreview = {
      config: sourcePreview.config,
      sourceSnapshot: sourcePreview.snapshot,
      snapshot: sqlResult.snapshot,
      queryResult: sqlResult.queryResult,
      option,
    };
    draftChart.setOption(option, { notMerge: true });
    elements.save.disabled = false;
    elements.validation.className = 'validation-message visible success';
    elements.validation.textContent = `${sqlResult.snapshot.source.length.toLocaleString()} chart points ready · changes remain local until Save`;
    const sqlStatus = document.querySelector('#sql-status');
    if (sqlStatus) {
      sqlStatus.className = 'sql-status success';
      sqlStatus.innerHTML = sourcePreview.config.sql.enabled
        ? `<i></i><span>${sqlResult.queryResult.selectedRows.toLocaleString()} SQL rows → ${sqlResult.queryResult.outputPoints.toLocaleString()} chart timestamps · SQLite ${escapeHtml(sqlResult.queryResult.sqliteVersion)}</span>`
        : '<i></i><span>SQL bypassed · generated rows pass directly to the chart</span>';
    }
  } catch (error) {
    if (sequence !== renderSequence) return;
    elements.save.disabled = true;
    elements.validation.className = 'validation-message visible error';
    elements.validation.textContent = error.message;
    const sqlStatus = document.querySelector('#sql-status');
    if (sqlStatus) {
      sqlStatus.className = 'sql-status error';
      sqlStatus.innerHTML = `<i></i><span>${escapeHtml(error.message)}</span>`;
    }
  }
}

function scheduleDraftRender() {
  window.clearTimeout(renderTimer);
  renderTimer = window.setTimeout(() => { void renderDraft(); }, 120);
}

function newSeries() {
  const template = createDefaultConfig(config.time.endMs).series[0];
  const index = config.series.length;
  const palette = ['#e76f51', '#2a9d8f', '#5b6ee1', '#d4a72c', '#8f5da2', '#3d6b57', '#b14b63', '#4f7cac'];
  return {
    ...template,
    id: `series-${Date.now().toString(36)}-${index + 1}`,
    name: `Signal ${String.fromCharCode(65 + index)}`,
    color: palette[index % palette.length],
    generator: {
      ...template.generator,
      baseline: 42 + index * 12,
      amplitude: 10 + index * 3,
      phase: Number((index * 0.8).toFixed(2)),
      seedOffset: index * 997,
    },
  };
}

function saveConfig() {
  try {
    if (!latestPreparedPreview) {
      throw new TypeError('Wait for the SQL preview to finish before saving.');
    }
    const documentToSave = createSavedDocument(config, Date.now(), latestPreparedPreview);
    const json = serializeDocument(documentToSave);
    const parsed = parseDocument(json);
    const persisted = persistJson(json);
    showSavedDocument(parsed, json, persisted ? 'Saved' : 'Saved for this session');
    elements.save.classList.remove('confirmed');
    void elements.save.offsetWidth;
    elements.save.classList.add('confirmed');
  } catch (error) {
    elements.validation.className = 'validation-message visible error';
    elements.validation.textContent = `Could not save: ${error.message}`;
  }
}

function showSavedDocument(document, json, actionLabel) {
  savedDocument = document;
  serializedDocument = json;
  savedChart.setOption(buildOptionFromDocument(document), { notMerge: true });
  elements.savedPlaceholder.hidden = true;
  elements.savedStatus.className = 'status-pill saved';
  elements.savedStatus.innerHTML = '<i></i>SAVED';
  const savedAt = new Date(document.savedAtMs).toLocaleString();
  const pointCount = document.snapshot?.source?.length ?? 0;
  const selectedRows = document.queryResult?.selectedRows ?? pointCount;
  elements.documentMeta.textContent = `${actionLabel} ${savedAt} · ${selectedRows.toLocaleString()} SQL rows · ${pointCount.toLocaleString()} chart points`;
  elements.jsonSize.textContent = `${new Blob([json]).size.toLocaleString()} bytes`;
  elements.jsonOutput.textContent = json.length > MAX_JSON_PREVIEW
    ? `${json.slice(0, MAX_JSON_PREVIEW)}\n\n… preview truncated; Download contains the complete document.`
    : json;
  elements.copy.disabled = false;
  elements.download.disabled = false;
}

async function copyJson() {
  if (!serializedDocument) return;
  try {
    await navigator.clipboard.writeText(serializedDocument);
    const original = elements.copy.textContent;
    elements.copy.textContent = 'Copied';
    window.setTimeout(() => { elements.copy.textContent = original; }, 1300);
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = serializedDocument;
    document.body.append(textArea);
    textArea.select();
    document.execCommand('copy');
    textArea.remove();
  }
}

function downloadJson() {
  if (!serializedDocument) return;
  const blob = new Blob([serializedDocument], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `echarts-config-${new Date(savedDocument.savedAtMs).toISOString().replaceAll(':', '-')}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

async function importJson(file) {
  try {
    const json = await file.text();
    const parsed = parseDocument(json);
    const canonicalJson = serializeDocument(parsed);
    const persisted = persistJson(canonicalJson);
    showSavedDocument(parsed, canonicalJson, persisted ? 'Opened' : 'Opened for this session');
  } catch (error) {
    elements.documentMeta.textContent = `Could not open JSON: ${error.message}`;
  } finally {
    elements.fileInput.value = '';
  }
}

elements.form.addEventListener('input', (event) => {
  const input = event.target.closest('[data-path]');
  if (!input) return;
  setAtPath(config, input.dataset.path, readInputValue(input));
  scheduleDraftRender();
});

elements.form.addEventListener('change', (event) => {
  const input = event.target.closest('[data-path]');
  if (!input) return;
  setAtPath(config, input.dataset.path, readInputValue(input));
  if (input.dataset.path.endsWith('.type') || input.dataset.path.endsWith('.name') || input.dataset.path.endsWith('.color')) {
    renderInspector();
  }
  scheduleDraftRender();
});

elements.form.addEventListener('click', (event) => {
  const button = event.target.closest('[data-action]');
  if (!button) return;
  const action = button.dataset.action;
  if (action === 'add-series' && config.series.length < 8) config.series.push(newSeries());
  if (action === 'remove-series' && config.series.length > 1) config.series.splice(Number(button.dataset.index), 1);
  if (action === 'regenerate') config.seed = (Number(config.seed) + 1) >>> 0;
  if (action === 'reset') config = createDefaultConfig();
  if (action === 'sql-preset' && SQL_PRESETS[button.dataset.preset]) {
    config.sql.enabled = true;
    config.sql.query = SQL_PRESETS[button.dataset.preset];
  }
  renderInspector();
  void renderDraft();
});

elements.save.addEventListener('click', saveConfig);
elements.copy.addEventListener('click', copyJson);
elements.download.addEventListener('click', downloadJson);
elements.open.addEventListener('click', () => elements.fileInput.click());
elements.fileInput.addEventListener('change', () => {
  const [file] = elements.fileInput.files;
  if (file) importJson(file);
});

window.addEventListener('keydown', (event) => {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
    event.preventDefault();
    if (!elements.save.disabled) saveConfig();
  }
});

window.addEventListener('resize', () => {
  draftChart.resize();
  savedChart.resize();
});

renderInspector();
void renderDraft();

const persistedJson = readPersistedJson();
if (persistedJson) {
  try {
    showSavedDocument(parseDocument(persistedJson), persistedJson, 'Restored');
  } catch {
    discardPersistedJson();
  }
}
